import { useState } from 'react';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { errorMessage, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, PageHeader, Spinner, Table, Td, dateTime, num } from '../../components/ui';

interface System {
  services: { database: boolean; redis: boolean; pades: boolean };
  email: { available: boolean; max_24h?: number; sent_24h?: number; sandbox?: boolean };
  configuration: Record<string, string | null>;
  migrations: { count: number; last: string } | null;
  suppressions: { email: string; reason: string; detail: string | null; created_at: string }[];
  failed_webhooks: { url: string; client: string; status_code: number | null; error_message: string | null; delivered_at: string }[];
}

const CONFIG_LABELS: Record<string, string> = {
  email_from: 'Expéditeur des e-mails',
  frontend_url: 'Adresse du portail',
  s3_bucket: 'Stockage S3',
  object_lock_mode: 'Verrouillage S3',
  object_lock_retention_days: 'Conservation (jours)',
  tsa_url: 'Horodatage (TSA)',
  alert_email: "Adresse d'alerte",
  node_env: 'Environnement',
};

function ServiceCard({ name, ok, detail }: { name: string; ok: boolean; detail: string }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-4 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
      {ok ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
      <div>
        <div className="text-sm font-semibold text-slate-900">{name}</div>
        <div className="text-xs text-slate-600">{ok ? 'Opérationnel' : 'Indisponible'} · {detail}</div>
      </div>
    </div>
  );
}

export default function SystemPage() {
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<System>('/system')).data);
  const [message, setMessage] = useState<string | null>(null);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;

  const unblock = async (email: string) => {
    if (!window.confirm(`Autoriser à nouveau l'envoi d'e-mails à ${email} ?`)) return;
    try {
      await operatorApi.delete(`/suppressions/${encodeURIComponent(email)}`);
      await reload();
    } catch (e) {
      setMessage(errorMessage(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Système"
        subtitle="Santé des services, envoi d'e-mails et configuration"
        actions={<Button variant="secondary" onClick={reload} loading={loading}><RefreshCw className="w-4 h-4" />Actualiser</Button>}
      />
      {message && <div className="mb-4"><ErrorBox message={message} /></div>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ServiceCard name="Base de données" ok={data.services.database} detail="PostgreSQL" />
        <ServiceCard name="File de travaux" ok={data.services.redis} detail="Redis" />
        <ServiceCard name="Service de signature" ok={data.services.pades} detail="PAdES" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Envoi d'e-mails (Amazon SES)">
          {!data.email.available ? (
            <p className="text-sm text-slate-600">Le quota SES n&apos;a pas pu être lu (droit IAM <code>ses:GetSendQuota</code> manquant ou service injoignable).</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Mode :</span>
                {data.email.sandbox ? <Badge tone="amber">Bac à sable : destinataires vérifiés uniquement</Badge> : <Badge tone="green">Production</Badge>}
              </div>
              <div className="text-sm text-slate-700">
                {num(data.email.sent_24h)} e-mail(s) envoyé(s) sur 24 h, plafond de {num(data.email.max_24h)} par 24 h.
              </div>
              {data.email.sandbox && (
                <p className="text-xs text-slate-500">
                  Tant que le compte est en bac à sable, les clients ne reçoivent pas leurs invitations. Demandez la sortie du bac à sable dans la console AWS SES.
                </p>
              )}
            </div>
          )}
        </Card>

        <Card title="Configuration">
          <dl className="divide-y divide-slate-100 text-sm">
            {Object.entries(CONFIG_LABELS).map(([key, label]) => (
              <div key={key} className="flex justify-between gap-4 py-2">
                <dt className="text-slate-500">{label}</dt>
                <dd className="break-all text-right text-slate-900">{data.configuration[key] ?? <span className="text-slate-400">non défini</span>}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-4 py-2">
              <dt className="text-slate-500">Migrations appliquées</dt>
              <dd className="text-slate-900">{data.migrations ? num(data.migrations.count) : '—'}</dd>
            </div>
          </dl>
        </Card>
      </div>

      <Card title="Adresses e-mail bloquées (rebonds et plaintes)" className="mt-6">
        {data.suppressions.length === 0 ? (
          <Empty>Aucune adresse bloquée.</Empty>
        ) : (
          <Table head={['Adresse', 'Motif', 'Depuis', '']}>
            {data.suppressions.map((s) => (
              <tr key={s.email}>
                <Td className="font-medium text-slate-900">{s.email}</Td>
                <Td><Badge tone={s.reason === 'complaint' ? 'red' : 'amber'}>{s.reason === 'bounce' ? 'Rebond' : s.reason === 'complaint' ? 'Plainte' : 'Manuel'}</Badge></Td>
                <Td className="text-slate-500">{dateTime(s.created_at)}</Td>
                <Td className="text-right"><Button variant="secondary" small onClick={() => unblock(s.email)}>Réautoriser</Button></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Webhooks en échec (24 h)" className="mt-6">
        {data.failed_webhooks.length === 0 ? (
          <Empty>Aucun échec.</Empty>
        ) : (
          <Table head={['Client', 'Adresse', 'Résultat', 'Date']}>
            {data.failed_webhooks.map((w, index) => (
              <tr key={index}>
                <Td>{w.client}</Td>
                <Td className="break-all text-xs">{w.url}</Td>
                <Td><Badge tone="red">{w.status_code ?? w.error_message ?? 'erreur'}</Badge></Td>
                <Td className="whitespace-nowrap text-slate-500">{dateTime(w.delivered_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
